import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  MessageSquare,
  Mail,
  Shield,
  TrendingUp,
  Clock,
  Download,
  Search,
  LogOut,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Activity,
  BarChart3,
  Globe,
  Eye,
  HardHat,
  Database,
  DollarSign,
  Loader2,
  Info,
  Star,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  X,
  BookOpen,
  ArrowUpFromLine,
  Settings,
  Save,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8002";
const ADMIN_ROUTE = import.meta.env.VITE_ADMIN_ROUTE || "console";

interface DashboardStats {
  total_sessions_today: number;
  total_sessions_week: number;
  total_sessions_month: number;
  unique_ips_today: number;
  questions_today: number;
  questions_week: number;
  questions_month: number;
  avg_response_time_ms: number;
  total_subscribers: number;
  new_subscribers_today: number;
  new_subscribers_week: number;
  bot_flags_today: number;
  captcha_triggers_today: number;
}

interface QALog {
  id: string;
  session_id: string;
  question: string;
  answer: string;
  response_time_ms: number | null;
  is_bot_suspected: boolean;
  created_at: string;
}

interface Subscriber {
  id: string;
  email: string;
  source_page: string | null;
  is_active: boolean;
  created_at: string;
}

interface PriceSourceStatus {
  source_name: string;
  last_sync: string | null;
  cache_valid: boolean;
  cache_ttl_hours: number;
  description?: string;
  provides?: string[];
  currency?: string;
}

type PriceSyncStatus = Record<string, PriceSourceStatus>;

interface ScheduledJob {
  id: string;
  name: string;
  next_run: string;
  trigger: string;
}

interface CuratedQAEntry {
  id: string;
  question: string;
  answer: string;
  improved_answer: string | null;
  category: string | null;
  tags: string[];
  ask_count: number;
  click_count: number;
  is_approved: boolean;
  accuracy_rating: number | null;
  admin_notes: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

type TabType =
  | "overview"
  | "qa"
  | "subscribers"
  | "traffic"
  | "bot"
  | "data"
  | "curated"
  | "settings";

interface SiteSettings {
  [key: string]: {
    value: string;
    description: string;
    updated_at: string | null;
    updated_by: string | null;
  };
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [rateLimiterStats, setRateLimiterStats] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [qaLogs, setQaLogs] = useState<QALog[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");

  // Data management state
  const [priceSyncStatus, setPriceSyncStatus] =
    useState<PriceSyncStatus | null>(null);
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJob[]>([]);
  const [isRefreshingPrices, setIsRefreshingPrices] = useState(false);
  const [priceRefreshMessage, setPriceRefreshMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Curated Q&A state
  const [curatedQA, setCuratedQA] = useState<CuratedQAEntry[]>([]);
  const [curatedTotal, setCuratedTotal] = useState(0);
  const [curatedLoading, setCuratedLoading] = useState(false);
  const [curatedPage, setCuratedPage] = useState(0);
  const [curatedSearch, setCuratedSearch] = useState("");
  const [curatedSortBy, setCuratedSortBy] = useState("ask_count");
  const [curatedApprovedOnly, setCuratedApprovedOnly] = useState(false);
  const [selectedQA, setSelectedQA] = useState<CuratedQAEntry | null>(null);
  const [editAnswer, setEditAnswer] = useState("");
  const [editRating, setEditRating] = useState<number>(0);
  const [editNotes, setEditNotes] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [curatedMessage, setCuratedMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [autoCurateLoading, setAutoCurateLoading] = useState(false);

  // Site settings state
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>(
    {},
  );

  // Helper to get CSRF token from cookie (for state-changing requests)
  const getCsrfToken = (): string | null => {
    const match = document.cookie.match(/csrf_token=([^;]+)/);
    return match ? match[1] : null;
  };

  // Fetch with credentials (cookies sent automatically)
  const fetchWithAuth = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(
      `${API_BASE}/api/v1/${ADMIN_ROUTE}${endpoint}`,
      {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        credentials: "include", // Send HTTP-only auth cookie automatically
      },
    );

    if (response.status === 401) {
      // Auth cookie expired/invalid - redirect to login
      navigate(`/${ADMIN_ROUTE}`);
      return null;
    }

    return response.json();
  };

  // POST/PUT/DELETE with CSRF protection
  const fetchWithCsrf = async (endpoint: string, options: RequestInit = {}) => {
    const csrfToken = getCsrfToken();
    return fetchWithAuth(endpoint, {
      ...options,
      headers: {
        ...options.headers,
        ...(csrfToken && { "X-CSRF-Token": csrfToken }),
      },
    });
  };

  const loadDashboardStats = async () => {
    try {
      const data = await fetchWithAuth("/stats");
      if (data) {
        setStats(data.stats);
        setRateLimiterStats(data.rate_limiter);
      }
    } catch {
      setError("Failed to load dashboard stats");
    }
  };

  const loadQALogs = async () => {
    try {
      const data = await fetchWithAuth("/qa/recent?limit=50");
      if (data) {
        setQaLogs(data.logs);
      }
    } catch {
      console.error("Failed to load Q&A logs");
    }
  };

  const loadSubscribers = async () => {
    try {
      const data = await fetchWithAuth("/subscribers?limit=100");
      if (data) {
        setSubscribers(data.subscribers);
      }
    } catch {
      console.error("Failed to load subscribers");
    }
  };

  const exportSubscribers = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/${ADMIN_ROUTE}/subscribers/export`,
        {
          credentials: "include", // Use HTTP-only cookie
        },
      );

      if (response.status === 401) {
        navigate(`/${ADMIN_ROUTE}`);
        return;
      }

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `subscribers-${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      console.error("Failed to export subscribers");
    }
  };

  // Data management functions
  const loadPriceSyncStatus = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/quantity-survey/prices/sync-status`,
      );
      if (response.ok) {
        const data = await response.json();
        setPriceSyncStatus(data.source_status || data);
      }
    } catch {
      console.error("Failed to load price sync status");
    }
  };

  const loadScheduledJobs = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/quantity-survey/prices/scheduled-jobs`,
      );
      if (response.ok) {
        const data = await response.json();
        setScheduledJobs(data.jobs || data);
      }
    } catch {
      console.error("Failed to load scheduled jobs");
    }
  };

  const handleRefreshPrices = async (source?: string) => {
    setIsRefreshingPrices(true);
    setPriceRefreshMessage(null);
    try {
      const params = new URLSearchParams();
      if (source) params.append("source", source);
      params.append("force", "true");

      const response = await fetch(
        `${API_BASE}/api/v1/quantity-survey/prices/refresh?${params.toString()}`,
        { method: "POST" },
      );

      if (!response.ok) {
        throw new Error("Refresh API unavailable");
      }

      const result = await response.json();
      await loadPriceSyncStatus();
      setPriceRefreshMessage({
        type: "success",
        text: result.message || "Prices refreshed successfully",
      });
    } catch (err) {
      setPriceRefreshMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to refresh prices",
      });
    } finally {
      setIsRefreshingPrices(false);
      setTimeout(() => setPriceRefreshMessage(null), 5000);
    }
  };

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear HTTP-only cookies
      await fetchWithCsrf("/logout", { method: "POST" });
    } catch (err) {
      // Continue with logout even if API call fails
      console.error("Logout API error:", err);
    }
    // Navigate to login page
    navigate(`/${ADMIN_ROUTE}`);
  };

  // Curated Q&A functions
  const fetchCuratedQA = async () => {
    setCuratedLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "20",
        offset: String(curatedPage * 20),
        sort_by: curatedSortBy,
      });
      if (curatedApprovedOnly) params.set("approved_only", "true");
      if (curatedSearch) params.set("search", curatedSearch);

      const data = await fetchWithAuth(`/qa/curated?${params}`);
      if (data) {
        setCuratedQA(data.entries || []);
        setCuratedTotal(data.total || 0);
      }
    } catch (e) {
      console.error("Failed to load curated Q&A:", e);
    } finally {
      setCuratedLoading(false);
    }
  };

  const handleApproveQA = async (qaId: string) => {
    try {
      const body: Record<string, unknown> = {};
      if (editAnswer) body.improved_answer = editAnswer;
      if (editRating > 0) body.accuracy_rating = editRating;
      if (editNotes) body.admin_notes = editNotes;
      if (editCategory) body.category = editCategory;

      await fetchWithCsrf(`/qa/curated/${qaId}/approve`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      setCuratedMessage({ type: "success", text: "Q&A approved successfully" });
      setSelectedQA(null);
      fetchCuratedQA();
    } catch {
      setCuratedMessage({ type: "error", text: "Failed to approve Q&A" });
    }
    setTimeout(() => setCuratedMessage(null), 4000);
  };

  const handleDeleteQA = async (qaId: string) => {
    if (!confirm("Are you sure you want to delete this curated Q&A entry?"))
      return;
    try {
      await fetchWithCsrf(`/qa/curated/${qaId}`, { method: "DELETE" });
      setCuratedMessage({ type: "success", text: "Q&A deleted successfully" });
      setSelectedQA(null);
      fetchCuratedQA();
    } catch {
      setCuratedMessage({ type: "error", text: "Failed to delete Q&A" });
    }
    setTimeout(() => setCuratedMessage(null), 4000);
  };

  const handleAutoCurate = async () => {
    setAutoCurateLoading(true);
    try {
      await fetchWithCsrf("/qa/curated/auto-curate", {
        method: "POST",
        body: JSON.stringify({ min_ask_count: 3 }),
      });
      setCuratedMessage({
        type: "success",
        text: "Auto-curation completed. Refresh to see new entries.",
      });
      fetchCuratedQA();
    } catch {
      setCuratedMessage({ type: "error", text: "Auto-curation failed" });
    } finally {
      setAutoCurateLoading(false);
    }
    setTimeout(() => setCuratedMessage(null), 4000);
  };

  const handleCurateFromLog = async (logId: string) => {
    try {
      await fetchWithCsrf(`/qa/curated/from-log/${logId}`, { method: "POST" });
      setCuratedMessage({
        type: "success",
        text: "Q&A promoted to curated list",
      });
    } catch {
      setCuratedMessage({ type: "error", text: "Failed to curate from log" });
    }
    setTimeout(() => setCuratedMessage(null), 4000);
  };

  const openQADetail = (entry: CuratedQAEntry) => {
    setSelectedQA(entry);
    setEditAnswer(entry.improved_answer || "");
    setEditRating(entry.accuracy_rating || 0);
    setEditNotes(entry.admin_notes || "");
    setEditCategory(entry.category || "");
  };

  // Site settings functions
  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await fetchWithAuth("/settings");
      if (data && data.settings) {
        setSiteSettings(data.settings);
        // Initialize editedSettings with current values
        const currentValues: Record<string, string> = {};
        Object.entries(data.settings).forEach(([key, val]) => {
          currentValues[key] = (val as { value: string }).value || "";
        });
        setEditedSettings(currentValues);
      }
    } catch (e) {
      console.error("Failed to load settings:", e);
      setSettingsMessage({ type: "error", text: "Failed to load settings" });
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    setSettingsMessage(null);
    try {
      await fetchWithCsrf("/settings", {
        method: "PUT",
        body: JSON.stringify({ settings: editedSettings }),
      });
      setSettingsMessage({
        type: "success",
        text: "Settings saved successfully",
      });
      // Refresh settings to get updated timestamps
      await fetchSettings();
    } catch (e) {
      console.error("Failed to save settings:", e);
      setSettingsMessage({ type: "error", text: "Failed to save settings" });
    } finally {
      setSettingsSaving(false);
    }
    setTimeout(() => setSettingsMessage(null), 5000);
  };

  const handleSettingChange = (key: string, value: string) => {
    setEditedSettings((prev) => ({ ...prev, [key]: value }));
  };

  const hasUnsavedChanges = () => {
    return Object.entries(editedSettings).some(([key, value]) => {
      const original = siteSettings[key]?.value || "";
      return value !== original;
    });
  };

  // Fetch settings when tab is active
  useEffect(() => {
    if (activeTab === "settings") {
      fetchSettings();
    }
  }, [activeTab]);

  // Fetch curated Q&A when tab is active or filters change
  useEffect(() => {
    if (activeTab === "curated") {
      fetchCuratedQA();
    }
  }, [activeTab, curatedPage, curatedSortBy, curatedApprovedOnly]);

  // Debounce curated search
  useEffect(() => {
    if (activeTab !== "curated") return;
    const timeout = setTimeout(() => {
      setCuratedPage(0);
      fetchCuratedQA();
    }, 400);
    return () => clearTimeout(timeout);
  }, [curatedSearch]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        loadDashboardStats(),
        loadQALogs(),
        loadSubscribers(),
        loadPriceSyncStatus(),
        loadScheduledJobs(),
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "qa", label: "Q&A Logs", icon: MessageSquare },
    { id: "curated", label: "Curated Q&A", icon: BookOpen },
    { id: "subscribers", label: "Subscribers", icon: Mail },
    { id: "traffic", label: "Traffic", icon: Globe },
    { id: "bot", label: "Bot Detection", icon: Shield },
    { id: "data", label: "Data Management", icon: Database },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  const TabInfo = ({
    title,
    description,
    tips,
  }: {
    title: string;
    description: string;
    tips?: string[];
  }) => (
    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-100 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0">
          <Info className="w-4 h-4 text-teal-600" />
        </div>
        <div>
          <h3 className="font-medium text-slate-800 mb-1">{title}</h3>
          <p className="text-sm text-slate-600">{description}</p>
          {tips && tips.length > 0 && (
            <ul className="mt-2 space-y-1">
              {tips.map((tip, i) => (
                <li
                  key={i}
                  className="text-xs text-slate-500 flex items-center gap-1"
                >
                  <span className="w-1 h-1 bg-teal-400 rounded-full" />
                  {tip}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  const StatCard = ({
    icon: Icon,
    label,
    value,
    subValue,
    color = "teal",
  }: {
    icon: typeof Users;
    label: string;
    value: string | number;
    subValue?: string;
    color?: string;
  }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-slate-900">
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          {subValue && (
            <p className="text-sm text-slate-400 mt-1">{subValue}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-6 h-6 text-teal-500 animate-spin" />
          <span className="text-slate-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500 rounded-lg">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">Admin Dashboard</h1>
                <p className="text-xs text-slate-500">Builder Buddy</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  loadDashboardStats();
                  loadQALogs();
                  loadSubscribers();
                }}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-teal-500 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && stats && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <TabInfo
              title="Dashboard Overview"
              description="Monitor your app's key metrics at a glance. This shows real-time usage statistics including sessions, questions asked, and subscriber growth."
              tips={[
                "Sessions track unique user visits based on browser sessions",
                "Questions count includes all chat interactions",
                "Click refresh in the header to update all stats",
              ]}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Sessions Today"
                value={stats.total_sessions_today}
                subValue={`${stats.unique_ips_today} unique IPs`}
              />
              <StatCard
                icon={MessageSquare}
                label="Questions Today"
                value={stats.questions_today}
                subValue={`${stats.questions_week} this week`}
              />
              <StatCard
                icon={Mail}
                label="Total Subscribers"
                value={stats.total_subscribers}
                subValue={`+${stats.new_subscribers_today} today`}
              />
              <StatCard
                icon={Clock}
                label="Avg Response Time"
                value={`${Math.round(stats.avg_response_time_ms)}ms`}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  Weekly Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Sessions</span>
                    <span className="font-medium">
                      {stats.total_sessions_week.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Questions</span>
                    <span className="font-medium">
                      {stats.questions_week.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">New Subscribers</span>
                    <span className="font-medium">
                      {stats.new_subscribers_week}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  Bot Detection
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Bot Flags Today</span>
                    <span
                      className={`font-medium ${stats.bot_flags_today > 0 ? "text-amber-600" : "text-green-600"}`}
                    >
                      {stats.bot_flags_today}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Captcha Triggers</span>
                    <span className="font-medium">
                      {stats.captcha_triggers_today}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Active IPs Tracked</span>
                    <span className="font-medium">
                      {(rateLimiterStats as any)?.active_clients_24h || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "qa" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <TabInfo
              title="Q&A Logs"
              description="View all questions users have asked and the AI-generated answers. Use this to monitor conversation quality and identify common questions."
              tips={[
                "Search by keywords to find specific topics",
                "Bot flags indicate potentially automated requests",
                "Response time helps identify slow queries",
              ]}
            />
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                {qaLogs
                  .filter(
                    (log) =>
                      !searchQuery ||
                      log.question
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()),
                  )
                  .map((log) => (
                    <div key={log.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {log.question}
                          </p>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                            {log.answer.substring(0, 200)}...
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleCurateFromLog(log.id)}
                            className="px-2 py-1 bg-teal-50 text-teal-700 text-xs rounded-full hover:bg-teal-100 transition-colors flex items-center gap-1"
                            title="Promote to Curated Q&A"
                          >
                            <ArrowUpFromLine className="w-3 h-3" />
                            Curate
                          </button>
                          {log.is_bot_suspected && (
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                              Bot?
                            </span>
                          )}
                          {log.response_time_ms && (
                            <span className="text-xs text-slate-400">
                              {log.response_time_ms}ms
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-slate-400">
                          {new Date(log.created_at).toLocaleString()}
                        </p>
                        {curatedMessage && (
                          <span
                            className={`text-xs ${curatedMessage.type === "success" ? "text-green-600" : "text-red-600"}`}
                          >
                            {curatedMessage.text}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "curated" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <TabInfo
              title="Curated Q&A"
              description="Manage frequently asked questions. Approve, edit, and improve answers that will be shown as trending questions to users."
              tips={[
                "Auto-curate finds questions asked 3+ times and adds them automatically",
                "Approved entries with improved answers provide better user experience",
                "Use the Curate button in Q&A Logs to promote individual questions",
              ]}
            />

            {/* Curated message banner */}
            {curatedMessage && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  curatedMessage.type === "success"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                {curatedMessage.text}
              </div>
            )}

            {/* Controls bar */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search curated questions..."
                    value={curatedSearch}
                    onChange={(e) => setCuratedSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <select
                  value={curatedSortBy}
                  onChange={(e) => {
                    setCuratedSortBy(e.target.value);
                    setCuratedPage(0);
                  }}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="ask_count">Sort: Most Asked</option>
                  <option value="created_at">Sort: Newest</option>
                  <option value="accuracy_rating">Sort: Rating</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={curatedApprovedOnly}
                    onChange={(e) => {
                      setCuratedApprovedOnly(e.target.checked);
                      setCuratedPage(0);
                    }}
                    className="rounded border-slate-300 text-teal-500 focus:ring-teal-500"
                  />
                  Approved only
                </label>
                <button
                  onClick={handleAutoCurate}
                  disabled={autoCurateLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm disabled:opacity-50"
                >
                  {autoCurateLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Auto-Curate
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {curatedLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
                  <span className="ml-2 text-slate-500">Loading...</span>
                </div>
              ) : curatedQA.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No curated Q&A entries found.</p>
                  <p className="text-sm mt-1">
                    Use Auto-Curate or promote questions from Q&A Logs.
                  </p>
                </div>
              ) : (
                <>
                  {/* Table header */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase">
                    <div className="col-span-5">Question</div>
                    <div className="col-span-2">Category</div>
                    <div className="col-span-1 text-center">Asked</div>
                    <div className="col-span-1 text-center">Approved</div>
                    <div className="col-span-1 text-center">Rating</div>
                    <div className="col-span-2 text-right">Actions</div>
                  </div>

                  {/* Table rows */}
                  <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                    {curatedQA.map((entry) => (
                      <div
                        key={entry.id}
                        className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-slate-50 cursor-pointer"
                        onClick={() => openQADetail(entry)}
                      >
                        <div className="col-span-5 truncate text-sm text-slate-800">
                          {entry.question}
                        </div>
                        <div className="col-span-2">
                          {entry.category && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                              {entry.category}
                            </span>
                          )}
                        </div>
                        <div className="col-span-1 text-center text-sm text-slate-600">
                          {entry.ask_count}
                        </div>
                        <div className="col-span-1 text-center">
                          {entry.is_approved ? (
                            <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                          ) : (
                            <span className="text-slate-300">--</span>
                          )}
                        </div>
                        <div className="col-span-1 text-center">
                          {entry.accuracy_rating ? (
                            <div className="flex items-center justify-center gap-0.5">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              <span className="text-xs text-slate-600">
                                {entry.accuracy_rating}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">--</span>
                          )}
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openQADetail(entry);
                            }}
                            className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                            title="View / Edit"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproveQA(entry.id);
                            }}
                            className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteQA(entry.id);
                            }}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Pagination */}
            {curatedTotal > 20 && (
              <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
                <span className="text-sm text-slate-500">
                  Showing {curatedPage * 20 + 1}-
                  {Math.min((curatedPage + 1) * 20, curatedTotal)} of{" "}
                  {curatedTotal}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCuratedPage((p) => Math.max(0, p - 1))}
                    disabled={curatedPage === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="text-sm text-slate-600 px-2">
                    Page {curatedPage + 1} of {Math.ceil(curatedTotal / 20)}
                  </span>
                  <button
                    onClick={() => setCuratedPage((p) => p + 1)}
                    disabled={(curatedPage + 1) * 20 >= curatedTotal}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Detail / Edit Modal */}
            {selectedQA && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
                  {/* Modal header */}
                  <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900 text-lg">
                      Curated Q&A Detail
                    </h3>
                    <button
                      onClick={() => setSelectedQA(null)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-slate-400" />
                    </button>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Question */}
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase">
                        Question
                      </label>
                      <p className="mt-1 text-slate-800 bg-slate-50 p-3 rounded-lg text-sm">
                        {selectedQA.question}
                      </p>
                    </div>

                    {/* Original answer */}
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase">
                        Original Answer
                      </label>
                      <p className="mt-1 text-slate-600 bg-slate-50 p-3 rounded-lg text-sm max-h-40 overflow-y-auto whitespace-pre-wrap">
                        {selectedQA.answer}
                      </p>
                    </div>

                    {/* Improved answer */}
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase">
                        Improved Answer (admin)
                      </label>
                      <textarea
                        value={editAnswer}
                        onChange={(e) => setEditAnswer(e.target.value)}
                        placeholder="Provide an improved answer..."
                        rows={4}
                        className="mt-1 w-full border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-y"
                      />
                    </div>

                    {/* Accuracy rating */}
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase">
                        Accuracy Rating
                      </label>
                      <div className="flex items-center gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setEditRating(star)}
                            className="p-1 transition-colors"
                          >
                            <Star
                              className={`w-6 h-6 ${
                                star <= editRating
                                  ? "text-amber-400 fill-amber-400"
                                  : "text-slate-200"
                              }`}
                            />
                          </button>
                        ))}
                        {editRating > 0 && (
                          <button
                            onClick={() => setEditRating(0)}
                            className="ml-2 text-xs text-slate-400 hover:text-slate-600"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase">
                        Category
                      </label>
                      <input
                        type="text"
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        placeholder="e.g. Building Code, Fire & Safety, Zoning..."
                        className="mt-1 w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    {/* Admin notes */}
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase">
                        Admin Notes
                      </label>
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Internal notes about this Q&A..."
                        rows={2}
                        className="mt-1 w-full border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-y"
                      />
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span>Asked {selectedQA.ask_count} times</span>
                      <span>Clicked {selectedQA.click_count} times</span>
                      <span>
                        Created{" "}
                        {new Date(selectedQA.created_at).toLocaleDateString()}
                      </span>
                      {selectedQA.is_approved && selectedQA.approved_at && (
                        <span className="text-green-500">
                          Approved{" "}
                          {new Date(
                            selectedQA.approved_at,
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Modal footer */}
                  <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
                    <button
                      onClick={() => handleDeleteQA(selectedQA.id)}
                      className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedQA(null)}
                        className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleApproveQA(selectedQA.id)}
                        className="flex items-center gap-2 px-5 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve & Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "subscribers" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-semibold text-slate-900">
                  Email Subscribers ({subscribers.length})
                </h3>
                <button
                  onClick={exportSubscribers}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
              <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                {subscribers.map((sub) => (
                  <div
                    key={sub.id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{sub.email}</p>
                      <p className="text-sm text-slate-500">
                        From: {sub.source_page || "unknown"} ·{" "}
                        {new Date(sub.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        sub.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {sub.is_active ? "Active" : "Unsubscribed"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "traffic" && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                icon={Activity}
                label="Monthly Sessions"
                value={stats.total_sessions_month}
              />
              <StatCard
                icon={Eye}
                label="Today's Unique IPs"
                value={stats.unique_ips_today}
              />
              <StatCard
                icon={TrendingUp}
                label="Weekly Questions"
                value={stats.questions_week}
              />
            </div>
            <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">
                Traffic Overview
              </h3>
              <p className="text-slate-500">
                Detailed traffic analytics coming soon. Check back for charts
                and source breakdowns.
              </p>
            </div>
          </motion.div>
        )}

        {activeTab === "bot" && rateLimiterStats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">
                    Flagged Clients
                  </h3>
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {(rateLimiterStats as any).flagged_clients || 0}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Shield className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Banned IPs</h3>
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {(rateLimiterStats as any).banned_clients || 0}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Active Today</h3>
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {(rateLimiterStats as any).active_clients_24h || 0}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">
                Rate Limit Config
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Per Minute</p>
                  <p className="text-xl font-bold text-slate-900">
                    {(rateLimiterStats as any).config?.requests_per_minute ||
                      10}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Per Hour</p>
                  <p className="text-xl font-bold text-slate-900">
                    {(rateLimiterStats as any).config?.requests_per_hour || 60}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Per Day</p>
                  <p className="text-xl font-bold text-slate-900">
                    {(rateLimiterStats as any).config?.requests_per_day || 200}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "data" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Price Data Header */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Material Price Data
                    </h3>
                    <p className="text-sm text-slate-500">
                      Manage live price sources for the Quantity Survey module
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRefreshPrices()}
                  disabled={isRefreshingPrices}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRefreshingPrices ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh All Prices
                </button>
              </div>

              {priceRefreshMessage && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm ${
                    priceRefreshMessage.type === "success"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}
                >
                  {priceRefreshMessage.text}
                </div>
              )}

              {/* Price Sources Grid */}
              {priceSyncStatus && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(priceSyncStatus).map(([key, status]) => (
                    <div
                      key={key}
                      className={`p-4 rounded-lg border ${
                        status.cache_valid
                          ? "bg-emerald-50 border-emerald-200"
                          : "bg-amber-50 border-amber-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              status.cache_valid
                                ? "bg-emerald-500"
                                : "bg-amber-500"
                            }`}
                          />
                          <span className="font-medium text-slate-800">
                            {status.source_name}
                          </span>
                          {status.currency && (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                status.currency === "CAD"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {status.currency}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRefreshPrices(key)}
                          disabled={isRefreshingPrices}
                          className="text-xs px-2 py-1 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors disabled:opacity-50"
                        >
                          Refresh
                        </button>
                      </div>

                      {status.description && (
                        <p className="text-xs text-slate-600 mb-2">
                          {status.description}
                        </p>
                      )}

                      {status.provides && status.provides.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {status.provides.map((item) => (
                            <span
                              key={item}
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/70 text-slate-700 border border-slate-200"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between text-[10px] text-slate-500 pt-2 border-t border-slate-200/50">
                        <span>
                          {status.last_sync
                            ? `Synced: ${new Date(status.last_sync).toLocaleDateString()} ${new Date(status.last_sync).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                            : "Never synced"}
                        </span>
                        <span>TTL: {status.cache_ttl_hours}h</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Scheduled Jobs */}
            {scheduledJobs.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Scheduled Updates
                    </h3>
                    <p className="text-sm text-slate-500">
                      Automated price refresh jobs
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {scheduledJobs.map((job) => (
                    <div
                      key={job.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-slate-800">{job.name}</p>
                        <p className="text-xs text-slate-500">{job.trigger}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">Next run:</p>
                        <p className="text-xs text-slate-500">
                          {new Date(job.next_run).toLocaleDateString()}{" "}
                          {new Date(job.next_run).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "settings" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <TabInfo
              title="Site Settings"
              description="Configure site-wide settings including analytics, maintenance mode, and custom scripts. Changes are saved to the database and persist across deployments."
              tips={[
                "Google Analytics ID should be in format G-XXXXXXXXXX",
                "Custom scripts are injected into the page head/body",
                "Enable maintenance mode to show a maintenance page to users",
              ]}
            />

            {/* Settings message banner */}
            {settingsMessage && (
              <div
                className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                  settingsMessage.type === "success"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                {settingsMessage.type === "success" ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                {settingsMessage.text}
              </div>
            )}

            {settingsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
                <span className="ml-2 text-slate-500">Loading settings...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Analytics Section */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        Analytics
                      </h3>
                      <p className="text-sm text-slate-500">
                        Configure tracking and analytics
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Google Analytics Measurement ID
                      </label>
                      <input
                        type="text"
                        value={editedSettings.google_analytics_id || ""}
                        onChange={(e) =>
                          handleSettingChange(
                            "google_analytics_id",
                            e.target.value,
                          )
                        }
                        placeholder="G-XXXXXXXXXX"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {siteSettings.google_analytics_id?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* General Settings Section */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <Settings className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        General Settings
                      </h3>
                      <p className="text-sm text-slate-500">
                        Basic site configuration
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Site Name
                      </label>
                      <input
                        type="text"
                        value={editedSettings.site_name || ""}
                        onChange={(e) =>
                          handleSettingChange("site_name", e.target.value)
                        }
                        placeholder="Builder Buddy"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {siteSettings.site_name?.description}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Support Email
                      </label>
                      <input
                        type="email"
                        value={editedSettings.support_email || ""}
                        onChange={(e) =>
                          handleSettingChange("support_email", e.target.value)
                        }
                        placeholder="support@example.com"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {siteSettings.support_email?.description}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Max Chat Messages Per Day
                      </label>
                      <input
                        type="number"
                        value={editedSettings.max_chat_messages_per_day || ""}
                        onChange={(e) =>
                          handleSettingChange(
                            "max_chat_messages_per_day",
                            e.target.value,
                          )
                        }
                        placeholder="100"
                        min="0"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {siteSettings.max_chat_messages_per_day?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Maintenance Mode Section */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        Maintenance Mode
                      </h3>
                      <p className="text-sm text-slate-500">
                        Temporarily disable site access
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editedSettings.maintenance_mode === "true"}
                          onChange={(e) =>
                            handleSettingChange(
                              "maintenance_mode",
                              e.target.checked ? "true" : "false",
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                      <span className="text-sm text-slate-700">
                        Enable Maintenance Mode
                      </span>
                      {editedSettings.maintenance_mode === "true" && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Active
                        </span>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Maintenance Message
                      </label>
                      <textarea
                        value={editedSettings.maintenance_message || ""}
                        onChange={(e) =>
                          handleSettingChange(
                            "maintenance_message",
                            e.target.value,
                          )
                        }
                        placeholder="We're currently performing scheduled maintenance..."
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-y"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {siteSettings.maintenance_message?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Custom Scripts Section */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Globe className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        Custom Scripts
                      </h3>
                      <p className="text-sm text-slate-500">
                        Inject custom code into pages
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Custom Head Scripts
                      </label>
                      <textarea
                        value={editedSettings.custom_head_scripts || ""}
                        onChange={(e) =>
                          handleSettingChange(
                            "custom_head_scripts",
                            e.target.value,
                          )
                        }
                        placeholder="<script>...</script>"
                        rows={4}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm font-mono text-xs resize-y"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {siteSettings.custom_head_scripts?.description}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Custom Body Scripts
                      </label>
                      <textarea
                        value={editedSettings.custom_body_scripts || ""}
                        onChange={(e) =>
                          handleSettingChange(
                            "custom_body_scripts",
                            e.target.value,
                          )
                        }
                        placeholder="<script>...</script>"
                        rows={4}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm font-mono text-xs resize-y"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {siteSettings.custom_body_scripts?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* API Settings Section */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Shield className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        API Settings
                      </h3>
                      <p className="text-sm text-slate-500">
                        Configure public API access
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedSettings.enable_public_api === "true"}
                        onChange={(e) =>
                          handleSettingChange(
                            "enable_public_api",
                            e.target.checked ? "true" : "false",
                          )
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                    <span className="text-sm text-slate-700">
                      Enable Public API Access
                    </span>
                    {editedSettings.enable_public_api === "true" && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                        Enabled
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    {siteSettings.enable_public_api?.description}
                  </p>
                </div>

                {/* Save Button */}
                <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 p-4">
                  <div className="text-sm text-slate-500">
                    {hasUnsavedChanges() ? (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        You have unsaved changes
                      </span>
                    ) : (
                      "All changes saved"
                    )}
                  </div>
                  <button
                    onClick={saveSettings}
                    disabled={settingsSaving || !hasUnsavedChanges()}
                    className="flex items-center gap-2 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {settingsSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save Settings
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
